import { SUPABASE_CONFIG } from '../config/supabase';
import { supabase } from '../config/supabase';

export interface Transaction {
  id: string;
  created_at: string;
  description: string;
  payment_type: string;
  amount: number;
  currency: string;
  status: string;
  credits_added: number;
  reference: string;
  display_amount?: string; // Pre-formatted amount from Edge Function
}

export interface UserCredits {
  total_credits: number;
  one_time_credits: number;
  subscription_credits: number;
}

export interface Subscription {
  id: string;
  subscription_code: string;
  status: string;
  current_period_end: string;
  next_payment_date: string;
  plan: {
    name: string;
    tour_credits: number;
  };
}

/**
 * Fetch user's transaction history
 */
export const getTransactionHistory = async (userId?: string): Promise<Transaction[]> => {
  try {
    // For testing: use test email
    const testEmail = 'test@tourbud.app';
    const testUserId = `temp_${testEmail.replace(/[^a-zA-Z0-9]/g, '_')}`;
    const finalUserId = userId || testUserId;

    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', finalUserId)
      .eq('status', 'success')
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch transaction history:', error);
    return [];
  }
};

/**
 * Get user's credit balance and subscription info
 */
export const getUserCredits = async (token?: string): Promise<{
  credits: UserCredits | null;
  subscription: Subscription | null;
  has_active_subscription: boolean;
}> => {
  try {
    const authToken = token || SUPABASE_CONFIG.anonKey;

    const response = await fetch(
      `${SUPABASE_CONFIG.url}${SUPABASE_CONFIG.endpoints.getUserCredits}`,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      }
    );

    const data = await response.json();

    if (data.status === 'success') {
      return {
        credits: data.credits,
        subscription: data.subscription,
        has_active_subscription: data.has_active_subscription,
      };
    }

    throw new Error(data.error || 'Failed to fetch user credits');
  } catch (error) {
    console.error('Error fetching user credits:', error);
    return {
      credits: null,
      subscription: null,
      has_active_subscription: false,
    };
  }
};

/**
 * Format amount for display (converts cents to dollars and adds $)
 */
export const formatAmount = (amount: number, currency: string = 'USD'): string => {
  const value = (amount / 100).toFixed(2);
  return `$${value}`;
};

/**
 * Format transaction for display
 */
export const formatTransaction = (transaction: Transaction) => {
  return {
    id: transaction.id,
    date: transaction.created_at,
    description: transaction.description,
    type: transaction.payment_type === 'subscription' ? 'subscription' : 'one-off',
    amount: formatAmount(transaction.amount, transaction.currency),
    credits: transaction.credits_added,
    status: transaction.status,
  };
};

const paymentService = {
  getTransactionHistory,
  getUserCredits,
  formatAmount,
  formatTransaction,
};

export default paymentService;

